import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Check, Phone, Clock, ExternalLink, Stethoscope, Microscope, Syringe, HeartPulse, Users, UserRound, ClipboardList, Snowflake, Brain, Activity, ShieldCheck, ChevronDown } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { clinicInfo, servicesFallback } from "../mock";
import useSeo from "../hooks/useSeo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICON_MAP = { Stethoscope, Microscope, Syringe, HeartPulse, Users, UserRound, ClipboardList, Snowflake, Brain, Activity, ShieldCheck };

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useSeo({
    title: service ? `${service.name} | Beaudesert General Practice` : "Service | Beaudesert General Practice",
    description: service?.description
      ? service.description.slice(0, 155)
      : "Comprehensive medical services at Beaudesert General Practice & Skin Cancer Clinic. Bulk billed with a valid Medicare card.",
    path: `/services/${slug}`
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    setError(false);
    axios
      .get(`${API}/services/${slug}`)
      .then((r) => setService(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  // Fallback name if API unreachable
  const displayName = service?.name || servicesFallback.find((s) => s.slug === slug)?.name || "Service";

  const Icon = ICON_MAP[service?.icon] || Stethoscope;

  return (
    <div className="relative bg-[#f0edfa] min-h-screen" style={{ overflowX: "clip" }}>
      <Navbar />

      {/* Hero */}
      <section className="relative px-4 md:px-8 pt-10 pb-20 md:pt-14 md:pb-28 molecule-bg">
        <div className="mx-auto max-w-[1200px]">
          <Link
            to="/#services"
            className="inline-flex items-center gap-2 text-[#1a2464] font-semibold text-sm mb-8 hover:text-[#3dc9b3]"
          >
            <ArrowLeft className="w-4 h-4" /> Back to all services
          </Link>

          {loading && <div className="h-40 rounded-3xl bg-white/60 animate-pulse" />}

          {error && !loading && (
            <div className="bg-white rounded-3xl p-10 text-center">
              <h1 className="section-title">Service not found</h1>
              <p className="text-[#4a4a63] mt-3">The service you're looking for isn't available. Please return to services.</p>
              <Link to="/#services" className="inline-block mt-6 bg-[#1a2464] text-white font-semibold px-6 py-3 rounded-full">Back to Services</Link>
            </div>
          )}

          {service && !loading && (
            <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-10 items-start">
              <div>
                <div className="inline-flex items-center gap-3 mb-6">
                  <span className="w-14 h-14 rounded-2xl bg-[#3dc9b3]/15 grid place-items-center">
                    <Icon className="w-7 h-7 text-[#3dc9b3]" />
                  </span>
                  <span className="eyebrow mb-0">Our Services</span>
                </div>
                <h1 className="section-title">{displayName}</h1>
                <p className="text-[#4a4a63] mt-6 leading-relaxed text-[15px] md:text-[16px] max-w-2xl">
                  {service.description}
                </p>

                <div className="flex flex-wrap gap-3 mt-8">
                  <a
                    href={clinicInfo.hotdocUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-2 bg-[#1a2464] text-white font-semibold pl-6 pr-2 py-2 rounded-full hover:bg-[#141c52]"
                  >
                    <span>Book on HotDoc</span>
                    <span className="w-9 h-9 rounded-full bg-[#3dc9b3] grid place-items-center group-hover:translate-x-0.5 transition-transform">
                      <ArrowRight className="w-4 h-4 text-white" />
                    </span>
                  </a>
                  <a
                    href={`tel:${clinicInfo.phoneRaw}`}
                    className="inline-flex items-center gap-2 bg-[#3dc9b3] text-white font-semibold px-5 py-3 rounded-full hover:bg-[#34b39f]"
                  >
                    <Phone className="w-4 h-4" /> {clinicInfo.phone}
                  </a>
                </div>
              </div>

              {/* Side card */}
              <div className="relative rounded-[28px] bg-[#1a2464] text-white overflow-hidden p-8">
                <div className="absolute inset-0 wave-pattern opacity-60 pointer-events-none" />
                <div className="relative space-y-6">
                  <div className="inline-flex items-center gap-2 bg-[#3dc9b3] text-white text-xs font-bold px-4 py-1.5 rounded-full">
                    <Check className="w-3.5 h-3.5" strokeWidth={3} /> 100% Bulk Billed · Medicare card
                  </div>
                  <div>
                    <div className="text-white/60 text-xs uppercase tracking-widest">Appointment duration</div>
                    <div className="text-2xl font-bold mt-1 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-[#3dc9b3]" /> {service.duration}
                    </div>
                  </div>
                  <div className="h-px bg-white/15" />
                  <div>
                    <div className="text-white/60 text-xs uppercase tracking-widest">Where</div>
                    <div className="font-bold mt-1">Beaudesert General Practice</div>
                    <div className="text-white/70 text-sm mt-1">35A William Street, Beaudesert QLD 4285</div>
                  </div>
                  <div className="h-px bg-white/15" />
                  <div>
                    <div className="text-white/60 text-xs uppercase tracking-widest">Hours</div>
                    <div className="text-white/80 text-sm mt-1 leading-relaxed">
                      {clinicInfo.hours.weekdays}<br />
                      {clinicInfo.hours.saturday}<br />
                      {clinicInfo.hours.sunday}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* What to expect + FAQ */}
      {service && !loading && (
        <section className="relative px-4 md:px-8 pb-24">
          <div className="mx-auto max-w-[1200px] grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <span className="eyebrow">What to expect</span>
              <h2 className="section-title text-3xl md:text-4xl">Your visit, step by step</h2>
              <ul className="mt-8 space-y-4">
                {service.expect?.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-0.5 shrink-0 w-6 h-6 rounded-full bg-[#3dc9b3]/15 grid place-items-center">
                      <Check className="w-3.5 h-3.5 text-[#3dc9b3]" strokeWidth={3} />
                    </span>
                    <span className="text-[#1a1a2e] text-[15px]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <span className="eyebrow">Frequently asked</span>
              <h2 className="section-title text-3xl md:text-4xl">Common questions</h2>
              <Accordion type="single" collapsible className="mt-8 space-y-3">
                {service.faqs?.map((f, i) => (
                  <AccordionItem
                    key={i}
                    value={`faq-${i}`}
                    className="bg-white rounded-2xl border border-[#eee6f7] px-5"
                  >
                    <AccordionTrigger className="text-left font-semibold text-[#1a2464] hover:no-underline py-4">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#4a4a63] text-sm leading-relaxed pb-4">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>

          {/* CTA */}
          <div className="mx-auto max-w-[1200px] mt-16 relative rounded-[32px] bg-[#1a2464] text-white overflow-hidden p-10 md:p-14 text-center">
            <div className="absolute inset-0 wave-pattern opacity-60 pointer-events-none" />
            <div className="relative">
              <h2 className="text-3xl md:text-4xl font-bold">Ready to book?</h2>
              <p className="text-white/75 mt-3 max-w-lg mx-auto">
                Book online via HotDoc or call reception — same-day appointments often available.
              </p>
              <div className="flex flex-wrap justify-center gap-3 mt-8">
                <a
                  href={clinicInfo.hotdocUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2 bg-[#3dc9b3] text-white font-semibold pl-6 pr-2 py-2 rounded-full hover:bg-[#34b39f]"
                >
                  <span>Book on HotDoc</span>
                  <span className="w-9 h-9 rounded-full bg-white/20 grid place-items-center group-hover:translate-x-0.5 transition-transform">
                    <ArrowRight className="w-4 h-4 text-white" />
                  </span>
                  <ExternalLink className="w-3.5 h-3.5 text-white/70" />
                </a>
                <a
                  href={`tel:${clinicInfo.phoneRaw}`}
                  className="inline-flex items-center gap-2 bg-white text-[#1a2464] font-semibold px-6 py-3 rounded-full hover:bg-white/90"
                >
                  <Phone className="w-4 h-4" /> {clinicInfo.phone}
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
